import React, {useEffect, useState} from "react";
import {UserIcon} from "@heroicons/react/24/solid";
import styles from "./ClientAccountUpdateCard.module.css";
import {clientInfoAPI, updateClientInfoAPI} from "../../../services/client";
import Input from '../../common/input/Input';
import Button from '../../common/button/Button';
import FileInput from '../../common/input/FileInput';
import BusinessNumberPopUp from "../../../views/client/popup/BusinessNumberPopUp";
import {checkAPI, mailAPI} from "../../../services/auth";
import {useAlert} from "../../common/error/AlertContext";
import Checkbox from "../../common/checkbox/Checkbox";
import OptionalPopUp from "../../common/term/OptionalPopUp";
import {getRegisterAPI} from "../../../services/term";

interface ClientAccountUpdateCardProps {
    onCancel: () => void;
}

interface UpdateForm {
    companyName: string,
    userName: string,
    password: string,
    managerName: string;
    position: string;
    phoneNumber: string;
    department: string;
    email: string,
    businessNumber: string,
    type: string,
    item: string,
    address: string,
    optional: boolean,
    license: File | null;
}

// 클라이언트 계정 정보 수정
const ClientAccountUpdateCard: React.FC<ClientAccountUpdateCardProps> = ({onCancel}) => {
    const [formData, setFormData] = useState<UpdateForm>({
        companyName: '',
        userName: '',
        password: '',
        email: '',
        businessNumber: '',
        license: null,
        managerName: '',
        position: '',
        phoneNumber: '',
        department: '',
        type: '',
        item: '',
        address: '',
        optional: false,
    });
    const [code, setCode] = useState('');
    const [confirmPassword, setConfirmPassword] = useState<string>('');
    const [emailVerified, setEmailVerified] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
    const [loading, setLoading] = useState(true);
    const [originalEmail, setOriginalEmail] = useState('');
    const {showAlert} = useAlert();
    const [optional, setOptional] = useState("");
    const [showOptional, setShowOptional] = useState(false);

    // 이메일 인증
    const isValidEmail = (email: string) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    };

    //전화번호 형식 확인
    const isValidPhoneNumber = (phone: string) => {
        // 한국 휴대폰 번호 패턴: 010-1234-5678, 010.1234.5678, 01012345678
        const phoneRegex = /^(010|011|016|017|018|019)[-.]?\d{3,4}[-.]?\d{4}$/;
        return phoneRegex.test(phone);
    };

    //전화번호 형식화 (자동으로 하이픈 추가)
    const formatPhoneNumber = (phone: string) => {
        // 숫자만 추출
        const numbers = phone.replace(/[^\d]/g, '');

        // 010으로 시작하는 경우
        if (numbers.startsWith('010') || numbers.startsWith('070')) {
            if (numbers.length <= 3) return numbers;
            if (numbers.length <= 7) return `${numbers.slice(0, 3)}-${numbers.slice(3)}`;
            return `${numbers.slice(0, 3)}-${numbers.slice(3, 7)}-${numbers.slice(7, 11)}`;
        }

        // 기타 번호 (011, 016, 017, 018, 019)
        if (numbers.length <= 3) return numbers;
        if (numbers.length <= 6) return `${numbers.slice(0, 3)}-${numbers.slice(3)}`;
        return `${numbers.slice(0, 3)}-${numbers.slice(3, 6)}-${numbers.slice(6, 10)}`;
    };

    //전화번호 유효성 검사
    const validatePhoneNumber = (phone: string) => {
        if (!phone.trim()) {
            return '전화번호를 입력해주세요.';
        }

        const numbers = phone.replace(/[^\d]/g, '');

        // 길이 검사
        if (numbers.length < 10 || numbers.length > 11) {
            return '전화번호는 10-11자리 숫자여야 합니다.';
        }

        // 유효한 시작 번호 검사
        if (!['010', '011', '016', '017', '018', '019', '070'].some(prefix => numbers.startsWith(prefix))) {
            return '유효하지 않은 전화번호 형식입니다.';
        }

        // 전체 형식 검사
        if (!isValidPhoneNumber(phone)) {
            return '올바른 전화번호 형식이 아닙니다.';
        }

        return null;
    };

    useEffect(() => {
        const fetchClientInfo = async () => {
            try {
                setLoading(true);
                // 클라이언트 정보 조회 API
                const data = await clientInfoAPI();
                setFormData({
                    companyName: data.companyName,
                    userName: data.userName,
                    password: '',
                    email: data.email,
                    businessNumber: data.businessNumber,
                    license: null,
                    managerName: data.managerName,
                    position: data.position,
                    phoneNumber: formatPhoneNumber(data.phoneNumber || ''), // 기존 전화번호도 포맷팅
                    department: data.department,
                    type: data.type,
                    item: data.item,
                    address: data.address,
                    optional: data.optional
                });
                setOriginalEmail(data.email);
                setConfirmPassword('');
            } catch (error) {
                console.log(error);
            } finally {
                setLoading(false);
            }
        };
        fetchClientInfo();
        fetchOptional();
    }, []);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const {name, value} = e.target;

        // 전화번호 입력 시 자동 포맷팅
        if (name === 'phoneNumber') {
            const formattedPhone = formatPhoneNumber(value);
            setFormData(prev => ({...prev, [name]: formattedPhone}));
        } else {
            setFormData(prev => ({...prev, [name]: value}));
        }
    };

    const handleConfirmPasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setConfirmPassword(e.target.value);
    };

    //사업자등록번호 인증 팝업 열기
    const handleOpenModal = () => {
        setIsModalOpen(true);
    };

    //사업자등록번호 인증 팝업 닫기
    const handleCloseModal = () => {
        setIsModalOpen(false);
    };

    //사업자등록증 수정
    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            setFormData(prev => ({...prev, license: file}));
        }
    };

    const handleEmailAuthentication = async () => {
        if (!isValidEmail(formData.email)) {
            // 메일 형식 인증
            showAlert("이메일 형식이 잘못되었습니다", "error");
            return;
        }
        try {
            // 인증 메일 전송 API
            await mailAPI(formData.email);
            showAlert("인증코드가 전송되었습니다.", "info");
        } catch (error) {
            console.log(error);
        }
    };

    const handleCodeCheck = async () => {
        try {
            // 메일 인증 API
            await checkAPI(formData.email, code);
            setEmailVerified(true);
            showAlert("인증되었습니다.", "info");
        } catch (error) {
            console.log(error);
        }
    };

    //비밀번호 조건 확인
    const validatePassword = (password: string) => {
        //비밀번호를 변경하지 않는 경우
        if(password.length === 0) return null;
        //최소 8자
        const minLength = 8;
        //문자 사용
        const hasLetter = /[a-zA-Z]/.test(password);
        //숫자 사용
        const hasNumber = /[0-9]/.test(password);
        //특수기호 사용
        const hasSpecial = /[!@#$%^&*(),.?":{}|<>]/.test(password);
        //에러 메세지
        if (password.length < minLength) {
            return '비밀번호는 8자 이상이어야 합니다.';
        }
        if (!hasLetter) {
            return '비밀번호에 문자가 포함되어야 합니다.';
        }
        if (!hasNumber) {
            return '비밀번호에 숫자가 포함되어야 합니다.';
        }
        if (!hasSpecial) {
            return '비밀번호에 특수문자가 포함되어야 합니다.';
        }
        return null;
    };

    const handleSubmit = async () => {
        //ID, PW, 사업자등록번호, 이메일, 사업자등록증, 전화번호 인증
        if (formData.userName.length > 10 || formData.userName.length < 4) {
            showAlert("ID는 4자 이상, 10자 이하여야 합니다", "error");
            return;
        }
        if (formData.password !== confirmPassword) {
            showAlert("비밀번호가 일치하지 않습니다.", "error");
            return;
        }
        const passwordError = validatePassword(formData.password);
        if (passwordError) {
            showAlert(passwordError, "error");
            return;
        }
        // 전화번호 유효성 검사 추가
        const phoneError = validatePhoneNumber(formData.phoneNumber);
        if (phoneError) {
            showAlert(phoneError, "error");
            return;
        }
        if (!formData.businessNumber.trim()) {
            showAlert('사업자등록번호가 인증되지 않았습니다', "error");
            return;
        }
        if (formData.email !== originalEmail && !emailVerified) {
            showAlert('이메일이 변경되었습니다. 인증을 완료해주세요.', "error");
            return;
        }
        try {
            const updateData = {
                ...formData,
                phoneNumber: formData.phoneNumber
            };

            if (!formData.optional) {
                updateData.address = '';
                updateData.department = '';
                updateData.position = '';
                updateData.type = '';
                updateData.item = '';
            }
            //클라이언트 계정 정보 수정 API
            await updateClientInfoAPI(updateData, formData.license);
            onCancel();
        } catch (error) {
            console.log(error);
        }
    };

    const fetchOptional = async () => {
        try {
            const optional = await getRegisterAPI();
            setOptional(optional);
        } catch (error) {
            console.error('Failed to fetch term: ', error);
        }
    }

    const handleAuthSuccess = (businessNumber: string) => {
        setFormData(prev => ({
            ...prev,
            businessNumber: businessNumber
        }));
    };

    const emailButton = () => {
        //이메일 수정 안할 경우 버튼 X
        if (formData.email === originalEmail) {
            return null;
        }
        //이메일 수정하고 인증 안된 경우 버튼 출력
        if (!emailVerified) {
            return (
                <Button
                    onClick={handleEmailAuthentication}
                    variant="secondary"
                    size="small"
                    disabled={!isValidEmail(formData.email)}
                >
                    Send
                </Button>
            );
        }
        return null;
    };

    if (loading) {
        return <div className={styles.loading}>Loading...</div>;
    }

    return (
        <>
            <div className={styles.container}>
                <div className={styles.cardWrapper}>
                    <div className={styles.licenseSection}>
                        <h2 className={styles.sectionTitle}>사업자등록증</h2>
                        <div className={styles.imageWrapper}>
                            {/*사업자등록증 조회*/}
                            {formData.license && (
                                <img
                                    src={URL.createObjectURL(formData.license)}
                                    alt="Business License Preview"
                                    className={styles.licenseImage}
                                />
                            )}
                        </div>
                        {/*사업자등록증 등록*/}
                        <FileInput
                            label=""
                            onChange={handleFileChange}
                            value={formData.license}
                            accept="image/*"
                        />
                    </div>

                    <div className={styles.accountCard}>
                        <div className={styles.header}>
                            <div className={styles.headerContent}>
                                <div className={styles.titleSection}>
                                    <UserIcon className={styles.userIcon}/>
                                    <h1 className={styles.title}>계정 정보</h1>
                                </div>
                                <div className={styles.buttonGroup}>
                                    {/*저장 버튼*/}
                                    <Button onClick={handleSubmit} variant="primary">
                                        저장
                                    </Button>
                                    {/*취소 버튼*/}
                                    <Button onClick={onCancel} variant="secondary">
                                        취소
                                    </Button>
                                </div>
                            </div>
                        </div>

                        <div className={styles.content}>
                            <div className={styles.formGrid}>
                                {/*회사명 입력창*/}
                                <Input
                                    label="회사명"
                                    name="companyName"
                                    value={formData.companyName}
                                    onChange={handleInputChange}
                                    fullWidth
                                    required
                                />
                                {/*ID 입력창*/}
                                <Input
                                    label="ID"
                                    name="userName"
                                    value={formData.userName}
                                    onChange={handleInputChange}
                                    fullWidth
                                    required
                                    writable={false}
                                />
                                {/*비밀번호 입력창*/}
                                <Input
                                    label="비밀번호"
                                    type="password"
                                    name="password"
                                    value={formData.password}
                                    onChange={handleInputChange}
                                    fullWidth
                                    required
                                />
                                {/*비밀번호 확인 입력창*/}
                                <Input
                                    label="비밀번호 확인"
                                    type="password"
                                    name="confirmPassword"
                                    value={confirmPassword}
                                    onChange={handleConfirmPasswordChange}
                                    fullWidth
                                    required
                                />
                                {/*담당자 성명 입력창*/}
                                <Input
                                    label="담당자 성명"
                                    name="managerName"
                                    value={formData.managerName}
                                    onChange={handleInputChange}
                                    fullWidth
                                    required
                                />
                                {/*담당자 전화번호 입력창*/}
                                <Input
                                    label="담당자 전화번호"
                                    name="phoneNumber"
                                    value={formData.phoneNumber}
                                    onChange={handleInputChange}
                                    placeholder="010-1234-5678"
                                    fullWidth
                                    required
                                />

                                {/* 선택항목 - 체크박스가 선택된 경우에만 표시 */}
                                {formData.optional && (
                                    <>
                                        {/*담당자 직위 입력창*/}
                                        <Input
                                            label="담당자 직위"
                                            name="position"
                                            value={formData.position}
                                            onChange={handleInputChange}
                                            fullWidth
                                            required
                                        />

                                        {/*담당자 부서 입력창*/}
                                        <Input
                                            label="담당자 부서"
                                            name="department"
                                            value={formData.department}
                                            onChange={handleInputChange}
                                            fullWidth
                                            required
                                        />

                                        {/*업태 입력창*/}
                                        <Input
                                            label="업태"
                                            name="type"
                                            value={formData.type}
                                            onChange={handleInputChange}
                                            fullWidth
                                            required
                                        />

                                        {/*종목 입력창*/}
                                        <Input
                                            label="종목"
                                            name="item"
                                            value={formData.item}
                                            onChange={handleInputChange}
                                            fullWidth
                                            required
                                        />

                                        {/*사업장 소재지 입력창*/}
                                        <Input
                                            label="사업장 소재지"
                                            name="address"
                                            value={formData.address}
                                            onChange={handleInputChange}
                                            fullWidth
                                            required
                                        />
                                    </>
                                )}

                                {/*이메일 입력창*/}
                                <Input
                                    label="Email"
                                    type="email"
                                    name="email"
                                    value={formData.email}
                                    rightButton={emailButton()}
                                    onChange={handleInputChange}
                                    fullWidth
                                    required
                                    disabled={emailVerified}
                                    writable={!emailVerified || formData.email === originalEmail}
                                />

                                {/*이메일 수정한 경우*/}
                                {formData.email !== originalEmail && (
                                    // 인증코드 입력창, 버튼 출력
                                    <Input
                                        label="인증 코드"
                                        name="code"
                                        value={code}
                                        onChange={(e) => setCode(e.target.value)}
                                        rightButton={
                                            !emailVerified && (
                                                <Button
                                                    onClick={handleCodeCheck}
                                                    variant="secondary"
                                                    size="small"
                                                >
                                                    Check
                                                </Button>
                                            )
                                        }
                                        className={`${styles.codeInput} ${emailVerified ? styles.verifiedInput : ''}`}
                                        required
                                        disabled={emailVerified}
                                        writable={!emailVerified}
                                    />
                                )}

                                {/*사업자등록번호 입력창*/}
                                <Input
                                    label="사업자등록번호"
                                    name="businessNumber"
                                    value={formData.businessNumber}
                                    writable={false}
                                    rightButton={
                                        <Button
                                            onClick={handleOpenModal}
                                            variant="secondary"
                                            size="small"
                                        >
                                            변경
                                        </Button>
                                    }
                                    onChange={handleInputChange}
                                    fullWidth
                                    required
                                />

                                <div className={styles.policy}>
                                    <label
                                        onClick={() => setShowOptional(true)}
                                        className={styles.policyLink}
                                    >개인정보 선택항목 수집·이용</label>
                                    <Checkbox
                                        label="동의"
                                        checked={formData.optional}
                                        onChange={(e) => {
                                            const isChecked = e.target.checked;
                                            setFormData(prev => ({
                                                ...prev,
                                                optional: isChecked
                                            }));
                                        }}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            {/*사업자등록번호 인증 팝업*/}
            <BusinessNumberPopUp
                isOpen={isModalOpen}
                onClose={handleCloseModal}
                onSuccess={handleAuthSuccess}
            />
            {showOptional && (
                <OptionalPopUp optional={optional} onClose={() => setShowOptional(false)}/>
            )}
        </>
    );
}

export default ClientAccountUpdateCard;